import React, { useEffect, useState, useRef, useCallback } from 'react';
import './Dashboard.css';
import api from './http';
import { Link } from 'react-router-dom';
import { useConnectivity } from './useConnectivity';
import { io } from 'socket.io-client';
import CompanyCard from './components/CompanyCard';
import StationCard from './components/StationCard';
import SessionDetailModal from './components/SessionDetailModal';
import SkillChecklistModal from './components/SkillChecklistModal';
import ProgressMatrix from './components/ProgressMatrix';

// Skills-based Testing Dashboard
export default function Dashboard() {
  // Core state
  const [testEvent, setTestEvent] = useState(null);
  const [stations, setStations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [companySummary, setCompanySummary] = useState([]);
  const [stationSkills, setStationSkills] = useState({});
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [availableProctors, setAvailableProctors] = useState([]);
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  // Staging timer state — tracks when each company entered staging
  const stagingEntryTimesRef = useRef({});
  const [stagingTick, setStagingTick] = useState(0); // ticks every second to refresh timers

  // UI state
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [draggingCompanyId, setDraggingCompanyId] = useState(null);
  const dragCompanyId = useRef(null);

  // Session detail modal
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [skillDetail, setSkillDetail] = useState(null);
  const [availableSkills, setAvailableSkills] = useState([]);

  // Connectivity
  const { serverReachable } = useConnectivity({ intervalMs: 10000, timeoutMs: 3000 });
  const [disconnectedSince, setDisconnectedSince] = useState(null);

  // Fetch available test events and workflow steps
  const fetchEvents = useCallback(async () => {
    try {
      const [eventsRes, stepsRes, procRes] = await Promise.all([
        api.get('/api/test-events'),
        api.get('/api/admin/workflow-steps'),
        api.get('/api/admin/proctors')
      ]);
      const events = Array.isArray(eventsRes.data) ? eventsRes.data : [];
      setAvailableEvents(events);
      setWorkflowSteps(Array.isArray(stepsRes.data) ? stepsRes.data : []);
      setAvailableProctors(Array.isArray(procRes.data) ? procRes.data : []);
      const activeEvent = events.find((e) => e.status === 'active');
      if (activeEvent) {
        setSelectedEventId(activeEvent.id);
      } else if (events.length > 0) {
        setSelectedEventId(events[0].id);
      }
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to fetch events' });
    }
  }, []);

  // Fetch dashboard data for selected event
  const refreshData = useCallback(async () => {
    if (!selectedEventId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/test-events/${selectedEventId}/full-dashboard`);
      const dash = res.data;

      const newStations = dash.stations || [];
      const newCompanies = dash.companies || [];

      setTestEvent(dash.event);
      setStations(newStations);
      setCompanySummary(dash.company_summary || []);
      setCompanies(newCompanies);
      setCandidates(dash.candidates || []);
      setActiveSessions(dash.active_sessions || []);

      // Maintain staging entry times — persisted to localStorage so timers
      // survive page refreshes and navigation away/back.
      const cacheKey = `stagingTimes_${selectedEventId}`;

      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        Object.keys(cached).forEach((id) => {
          if (!stagingEntryTimesRef.current[id]) {
            stagingEntryTimesRef.current[id] = cached[id];
          }
        });
      } catch (e) { /* ignore parse errors */ }

      const atStationIds = new Set(
        newStations.filter((s) => s.current_company_id).map((s) => s.current_company_id)
      );
      const STAGING_STATUSES_LOCAL = new Set(['registered', 'checked_in', 'makeup_active', 'review_ready', 'makeup_eligible']);
      const nowStagingIds = newCompanies
        .filter((c) => !atStationIds.has(c.id) && STAGING_STATUSES_LOCAL.has(c.status))
        .map((c) => c.id);
      const nowStagingSet = new Set(nowStagingIds);
      const now = Date.now();
      const updatedTimes = { ...stagingEntryTimesRef.current };
      nowStagingIds.forEach((id) => {
        if (!updatedTimes[id]) updatedTimes[id] = now;
      });
      Object.keys(updatedTimes).forEach((id) => {
        if (!nowStagingSet.has(parseInt(id))) delete updatedTimes[id];
      });
      stagingEntryTimesRef.current = updatedTimes;

      try {
        localStorage.setItem(cacheKey, JSON.stringify(updatedTimes));
      } catch (e) { /* ignore storage errors */ }

      const skillsMap = {};
      (dash.stations || []).forEach((s) => {
        skillsMap[s.id] = s.skills || [];
      });
      setStationSkills(skillsMap);

      if (disconnectedSince) setDisconnectedSince(null);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Error refreshing data' });
      if (!disconnectedSince) setDisconnectedSince(Date.now());
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, disconnectedSince]);

  // Initial load
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Refresh when event changes
  useEffect(() => {
    if (selectedEventId) {
      refreshData();
    }
  }, [selectedEventId, refreshData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(refreshData, 30000);
    return () => clearInterval(interval);
  }, [refreshData]);

  // Tick every second so staging timers update in the UI
  useEffect(() => {
    const interval = setInterval(() => setStagingTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Reconnection-triggered refresh
  useEffect(() => {
    if (serverReachable === true && disconnectedSince) {
      refreshData();
    }
  }, [serverReachable, disconnectedSince, refreshData]);

  // WebSocket Live Updates
  useEffect(() => {
    const socket = io(process.env.NODE_ENV === 'production' ? '/' : 'http://localhost:3000', {
      withCredentials: true
    });

    socket.on('connect', () => {
      socket.emit('join_command');
    });

    const handleEvent = () => refreshData();
    socket.on('company:assigned', handleEvent);
    socket.on('proctor:reassigned', handleEvent);
    socket.on('session:status', handleEvent);
    socket.on('skill:result', handleEvent);

    return () => {
      socket.disconnect();
    };
  }, [refreshData]);

  // Clear alert after 5 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const getCompanyNumber = (name) => {
    const match = name?.match(/\d+/);
    return match ? parseInt(match[0], 10) : 9999;
  };

  // eslint-disable-next-line no-unused-vars
  const getStagingTimer = (companyId) => {
    void stagingTick;
    const entry = stagingEntryTimesRef.current[companyId];
    if (!entry) return '00:00';
    const diffSec = Math.floor((Date.now() - entry) / 1000);
    const mins = Math.floor(diffSec / 60);
    const secs = diffSec % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Tier order: registered (still testing) → makeup_active → review_ready → makeup_eligible
  const STAGING_TIER = { registered: 0, makeup_active: 1, review_ready: 2, makeup_eligible: 3 };
  const STAGING_STATUSES = new Set(['registered', 'checked_in', 'makeup_active', 'review_ready', 'makeup_eligible']);

  const stagingCompanies = companies
    .filter((c) => {
      const atStation = stations.some((s) => s.current_company_id === c.id);
      return !atStation && STAGING_STATUSES.has(c.status);
    })
    .sort((a, b) => {
      const tierA = STAGING_TIER[a.status] ?? 0;
      const tierB = STAGING_TIER[b.status] ?? 0;
      if (tierA !== tierB) return tierA - tierB;
      const tA = stagingEntryTimesRef.current[a.id] ?? Infinity;
      const tB = stagingEntryTimesRef.current[b.id] ?? Infinity;
      if (tA !== tB) return tA - tB;
      return getCompanyNumber(a.name) - getCompanyNumber(b.name);
    });

  const passedCompanies = companies.filter((c) => c.status === 'passed');

  const computeRecommendations = () => {
    const recs = {};
    const usedCompanyIds = new Set();
    const emptyStations = stations.filter((s) => !s.current_company_id);
    const regularCompanies = stagingCompanies.filter((c) => c.status === 'registered');

    emptyStations.forEach((station) => {
      const eligible = regularCompanies.find((company) => {
        if (usedCompanyIds.has(company.id)) return false;
        const result = company.station_results?.[station.id];
        return !result || result === 'blank';
      });
      if (eligible) {
        recs[station.id] = { companyId: eligible.id, companyName: eligible.name };
        usedCompanyIds.add(eligible.id);
      }
    });
    return recs;
  };

  const stationRecommendations = computeRecommendations();
  const recommendedCompanyIds = new Set(
    Object.values(stationRecommendations).map((r) => r.companyId)
  );

  const getStatusCount = (status) => {
    const item = companySummary.find((s) => s.status === status);
    return item ? parseInt(item.count) : 0;
  };

  const dispatchCompany = async (companyId, stationId) => {
    try {
      const opId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await api.post(
        '/api/test-sessions/dispatch',
        { test_event_id: selectedEventId, company_id: companyId, station_id: stationId },
        { headers: { 'X-Op-Id': opId } }
      );
      setAlert({ type: 'success', message: 'Company dispatched to station!' });
      refreshData();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to dispatch company';
      setAlert({ type: 'danger', message: msg });
    }
  };

  const beginMakeups = async (companyId) => {
    try {
      await api.post(`/api/test-events/${selectedEventId}/companies/${companyId}/begin-makeups`);
      setAlert({ type: 'success', message: 'Makeup mode started — dispatch to failed stations.' });
      refreshData();
    } catch (err) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to begin makeups' });
    }
  };

  const moveToPassed = async (companyId) => {
    try {
      await api.post(`/api/test-events/${selectedEventId}/companies/${companyId}/move-to-passed`);
      setAlert({ type: 'success', message: 'Company moved to Passed.' });
      refreshData();
    } catch (err) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to move company to passed' });
    }
  };

  const advanceSessionStage = async (sessionId, stepKey) => {
    if (!sessionId || !stepKey) return;
    try {
      const opId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const res = await api.post(
        '/api/test-sessions/advance',
        { session_id: sessionId, step_key: stepKey },
        { headers: { 'X-Op-Id': opId } }
      );
      const stepLabel = res.data?.step?.label || stepKey;
      setAlert({ type: 'success', message: `Advanced to ${stepLabel}!` });
      refreshData();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to advance stage';
      setAlert({ type: 'danger', message: msg });
    }
  };

  const assignProctorToStation = async (stationId, proctorId) => {
    try {
      await api.put(`/api/test-events/${selectedEventId}/stations/${stationId}/proctor`, {
        proctor_id: proctorId
      });
      setAlert({ type: 'success', message: 'Proctor updated successfully!' });
      refreshData();
    } catch (err) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to update proctor' });
      refreshData();
    }
  };

  const checkInCompany = async (sessionId) => {
    try {
      await api.post('/api/test-sessions/check-in', { session_id: sessionId });
      setAlert({ type: 'success', message: 'Company checked in!' });
      refreshData();
      if (selectedSession === sessionId) loadSessionDetail(sessionId);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to check in company' });
    }
  };

  const startSession = async (sessionId) => {
    try {
      await api.post('/api/test-sessions/start', { session_id: sessionId });
      setAlert({ type: 'success', message: 'Testing started!' });
      refreshData();
      if (selectedSession === sessionId) loadSessionDetail(sessionId);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to start testing' });
    }
  };

  const completeSession = async (sessionId) => {
    try {
      await api.post('/api/test-sessions/complete', { session_id: sessionId });
      setAlert({ type: 'success', message: 'Session completed!' });
      setSelectedSession(null);
      setSessionDetail(null);
      refreshData();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to complete session' });
    }
  };

  const returnToStaging = async (sessionId) => {
    try {
      await api.post('/api/test-sessions/return', { session_id: sessionId });
      setAlert({ type: 'success', message: 'Company returned to staging!' });
      setSelectedSession(null);
      setSessionDetail(null);
      refreshData();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to return to staging' });
    }
  };

  const loadSessionDetail = async (sessionId) => {
    try {
      const [sessionRes, skillsRes] = await Promise.all([
        api.get(`/api/test-sessions/${sessionId}`),
        api.get('/api/skills?active_only=1')
      ]);
      setSessionDetail(sessionRes.data);
      setAvailableSkills(Array.isArray(skillsRes.data) ? skillsRes.data : []);
      setSelectedSession(sessionId);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to load session details' });
    }
  };

  const assignSkill = async (sessionId, candidateId, skillId) => {
    try {
      await api.post(`/api/test-sessions/${sessionId}/assign-skill`, {
        candidate_id: candidateId,
        skill_id: skillId
      });
      loadSessionDetail(sessionId);
      setAlert({ type: 'success', message: 'Skill assigned!' });
    } catch (err) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to assign skill' });
    }
  };

  const startSkillTest = async (sessionId, assignmentId) => {
    try {
      await api.post(`/api/test-sessions/${sessionId}/start-skill`, { assignment_id: assignmentId });
      loadSessionDetail(sessionId);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to start skill test' });
    }
  };

  const loadSkillDetail = async (skillId, assignmentId) => {
    try {
      const res = await api.get(`/api/skills/${skillId}`);
      setSkillDetail({ ...res.data, assignmentId });
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to load skill details' });
    }
  };

  const recordChecklistItem = async (sessionId, assignmentId, checklistItemId, result) => {
    try {
      await api.post(`/api/test-sessions/${sessionId}/record-checklist`, {
        assignment_id: assignmentId,
        checklist_item_id: checklistItemId,
        result
      });
      loadSessionDetail(sessionId);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to record result' });
    }
  };

  const completeSkillTest = async (sessionId, assignmentId, result) => {
    try {
      await api.post(`/api/test-sessions/${sessionId}/complete-skill`, {
        assignment_id: assignmentId,
        result
      });
      loadSessionDetail(sessionId);
      setSkillDetail(null);
      setAlert({ type: 'success', message: `Skill marked as ${result}!` });
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to complete skill test' });
    }
  };

  const onCompanyDragStart = (companyId) => {
    dragCompanyId.current = companyId;
    setDraggingCompanyId(companyId);
  };

  const onCompanyDragEnd = () => {
    dragCompanyId.current = null;
    setDraggingCompanyId(null);
  };

  const onStationDrop = (stationId) => {
    if (!dragCompanyId.current) return;
    const companyId = dragCompanyId.current;
    dragCompanyId.current = null;
    setDraggingCompanyId(null);

    const station = stations.find((s) => s.id === stationId);
    if (station?.current_company_id) {
      setAlert({ type: 'warning', message: 'Station is occupied' });
      return;
    }

    const company = companies.find((c) => c.id === companyId);
    if (company?.status !== 'registered') return;

    const priorResult = company?.station_results?.[stationId];
    if (priorResult && priorResult !== 'blank') {
      setAlert({ type: 'warning', message: 'Company has already completed this station' });
      return;
    }

    dispatchCompany(companyId, stationId);
  };

  const getStationSession = (stationId) => activeSessions.find((s) => s.station_id === stationId);

  const handleToggleCompany = (companyId) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  if (loading && !testEvent) {
    return (
      <div className="dashboard-root">
        <div className="header">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  const showOfflineBanner = serverReachable === false || !!disconnectedSince;

  return (
    <div className="dashboard-root">
      <div className="header">
        <h1>🔥 Firefighter Skills Testing Dashboard</h1>

        <div style={{ margin: '12px 0' }}>
          <select
            value={selectedEventId || ''}
            onChange={(e) => setSelectedEventId(parseInt(e.target.value))}
            style={{ padding: '8px 16px', fontSize: '1em', borderRadius: 8 }}
          >
            {availableEvents.map((evt) => (
              <option key={evt.id} value={evt.id}>
                {evt.name} ({evt.status})
              </option>
            ))}
          </select>
        </div>

        {testEvent && (
          <>
            <div style={{ fontSize: '0.95em', color: '#666', marginBottom: 8 }}>
              {testEvent.description} • {new Date(testEvent.event_date).toLocaleDateString()}
            </div>
            <div className="status-bar">
              <div className="status-item">Stations: {stations.length}</div>
              <div className="status-item">Companies: {companies.length}</div>
              <div className="status-item" style={{ background: '#d4edda' }}>Staging: {getStatusCount('registered')}</div>
              <div className="status-item" style={{ background: '#fff3cd' }}>Testing: {getStatusCount('testing') + getStatusCount('checked_in')}</div>
              {getStatusCount('makeup_eligible') > 0 && (
                <div className="status-item" style={{ background: '#fed7d7', color: '#c53030' }}>Needs Makeups: {getStatusCount('makeup_eligible')}</div>
              )}
              {getStatusCount('makeup_active') > 0 && (
                <div className="status-item" style={{ background: '#feebc8', color: '#c05621' }}>Makeups: {getStatusCount('makeup_active')}</div>
              )}
              {getStatusCount('review_ready') > 0 && (
                <div className="status-item" style={{ background: '#c6f6d5', color: '#276749' }}>Ready to Pass: {getStatusCount('review_ready')}</div>
              )}
              {getStatusCount('passed') > 0 && (
                <div className="status-item" style={{ background: '#9ae6b4', color: '#276749' }}>Passed: {getStatusCount('passed')}</div>
              )}
              <div className="status-item">Active Sessions: {activeSessions.length}</div>
            </div>
          </>
        )}

        <div style={{ marginTop: 18 }}>
          <Link to="/admin" className="btn" style={{ textDecoration: 'none', fontWeight: 600, marginRight: 12 }}>
            Admin Hub
          </Link>
          <button className="btn" onClick={refreshData} style={{ marginRight: 12 }}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {showOfflineBanner && (
        <div
          className="offline-banner"
          style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '8px 12px',
            border: '1px solid #ffeeba',
            borderRadius: 4,
            margin: '8px 16px'
          }}
        >
          <span>⚠️ Connection issues detected. Some features may be unavailable.</span>
        </div>
      )}

      {alert && (
        <div
          className={`alert ${alert.type}`}
          style={{
            margin: '0 16px 16px',
            padding: '12px 16px',
            borderRadius: 8,
            background:
              alert.type === 'success' ? '#d4edda' : alert.type === 'warning' ? '#fff3cd' : '#f8d7da',
            color:
              alert.type === 'success' ? '#155724' : alert.type === 'warning' ? '#856404' : '#721c24'
          }}
        >
          {alert.message}
          <button
            onClick={() => setAlert(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em' }}
          >
            ✕
          </button>
        </div>
      )}

      <div className="main-content" style={{ padding: '0 16px' }}>
        <section className="section stations-section">
          <h2>🏢 Testing Stations</h2>
          <div className="stations-grid">
            {stations.map((station) => {
              const session = getStationSession(station.id);
              const company = station.current_company_id
                ? companies.find((c) => c.id === station.current_company_id) || {
                    name: station.current_company_name,
                    status: 'testing',
                    candidate_names: [],
                    program_name: 'Unknown'
                  }
                : null;

              return (
                <StationCard
                  key={station.id}
                  station={station}
                  session={session}
                  company={company}
                  isDragTarget={!!draggingCompanyId}
                  onDrop={() => onStationDrop(station.id)}
                  onAdvanceStage={(action) => advanceSessionStage(session?.id, action)}
                  onViewDetails={() => loadSessionDetail(session?.id)}
                  workflowSteps={workflowSteps}
                  availableProctors={availableProctors}
                  onAssignProctor={assignProctorToStation}
                  recommendation={stationRecommendations[station.id]}
                  onAssignRecommended={(stationId, companyId) => dispatchCompany(companyId, stationId)}
                />
              );
            })}
          </div>
        </section>

        <section className="section staging-section">
          <h2>⏳ Staging Area ({stagingCompanies.length})</h2>
          <div className="staging-area">
            {stagingCompanies.length === 0 ? (
              <div style={{ color: '#888', padding: 16 }}>No companies in staging</div>
            ) : (() => {
              let lastTier = null;
              const TIER_LABELS = {
                0: null,
                1: { label: 'Makeups In Progress', color: '#c05621', bg: '#fffaf0', border: '#f6ad55' },
                2: { label: 'Ready to Pass', color: '#276749', bg: '#f0fff4', border: '#9ae6b4' },
                3: { label: 'Needs Makeups', color: '#c53030', bg: '#fff5f5', border: '#fc8181' }
              };
              const items = [];
              stagingCompanies.forEach((company) => {
                const tier = STAGING_TIER[company.status] ?? 0;
                if (tier !== lastTier && TIER_LABELS[tier]) {
                  const tl = TIER_LABELS[tier];
                  items.push(
                    <div
                      key={`divider-${tier}`}
                      style={{
                        width: '100%',
                        padding: '4px 10px',
                        margin: '8px 0 4px',
                        fontSize: '0.78em',
                        fontWeight: 700,
                        color: tl.color,
                        background: tl.bg,
                        border: `1px solid ${tl.border}`,
                        borderRadius: 4
                      }}
                    >
                      {tl.label}
                    </div>
                  );
                  lastTier = tier;
                }
                items.push(
                  <CompanyCard
                    key={company.id}
                    company={company}
                    onDragStart={company.status === 'registered' ? () => onCompanyDragStart(company.id) : undefined}
                    onDragEnd={onCompanyDragEnd}
                    isDragging={draggingCompanyId === company.id}
                    stagingEnteredAt={company.staging_entered_at}
                    isRecommended={recommendedCompanyIds.has(company.id)}
                    stations={stations}
                    requiredStations={testEvent?.required_stations}
                    onBeginMakeups={() => beginMakeups(company.id)}
                    onMoveToPassed={() => moveToPassed(company.id)}
                    onDispatchToStation={(stationId) => dispatchCompany(company.id, stationId)}
                  />
                );
              });
              return items;
            })()}
          </div>
        </section>

        {passedCompanies.length > 0 && (
          <section className="section staging-section">
            <h2>✅ Passed ({passedCompanies.length})</h2>
            <div className="staging-area">
              {passedCompanies.map((company) => (
                <div
                  key={company.id}
                  className="company-card"
                  style={{ borderLeft: '4px solid #48bb78', opacity: 0.85 }}
                >
                  <div className="company-header">
                    <div className="company-name">{company.name}</div>
                    <span style={{ fontSize: '0.8em', color: '#276749', fontWeight: 600 }}>PASSED</span>
                  </div>
                  <div className="company-candidates">
                    {company.candidate_names ? company.candidate_names.join('/') : ''}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <ProgressMatrix
        companies={companies}
        candidates={candidates}
        stations={stations}
        expandedCompanies={expandedCompanies}
        onToggleCompany={handleToggleCompany}
      />

      <SessionDetailModal
        sessionId={selectedSession}
        sessionDetail={sessionDetail}
        stationSkills={stationSkills}
        availableSkills={availableSkills}
        onClose={() => { setSelectedSession(null); setSessionDetail(null); setSkillDetail(null); }}
        onCheckIn={checkInCompany}
        onStartSession={startSession}
        onAssignSkill={assignSkill}
        onStartSkillTest={startSkillTest}
        onLoadSkillDetail={loadSkillDetail}
        onCompleteSkillTest={completeSkillTest}
        onReturnToStaging={returnToStaging}
        onCompleteSession={completeSession}
      />

      <SkillChecklistModal
        skillDetail={skillDetail}
        sessionId={selectedSession}
        onClose={() => setSkillDetail(null)}
        onRecordItem={recordChecklistItem}
        onCompleteSkillTest={completeSkillTest}
      />
    </div>
  );
}
